import React, { useRef, useState } from 'react';
import { ProTable } from '@ant-design/pro-components';
import { Button, Tag, Input, Space, Modal, Avatar, Spin, message, Empty } from 'antd';
import { SearchOutlined, RobotOutlined, UserOutlined, MessageOutlined, FireOutlined, QuestionCircleOutlined } from '@ant-design/icons';
import type { ProColumns, ActionType } from '@ant-design/pro-components';
import dayjs from 'dayjs';
import { getAdminSessions, getSessionMessages, AdminSession, SessionMessage, FoodItem, FoodResult, UserResult, WeightResult, ExerciseResult, LLMAnalysisResult } from '../../api/sessions';

const statusMap: Record<string, { color: string; text: string }> = {
  completed: { color: 'green', text: '已完成' },
  processing: { color: 'blue', text: '处理中' },
  recording: { color: 'orange', text: '录音中' },
  waiting_clarification: { color: 'purple', text: '等待澄清' },
  failed: { color: 'red', text: '失败' },
};

const mealTypeLabel: Record<string, { label: string; color: string }> = {
  breakfast: { label: '早餐', color: 'orange' },
  lunch: { label: '午餐', color: 'green' },
  dinner: { label: '晚餐', color: 'blue' },
  snack: { label: '零食', color: 'purple' },
};

const FoodItemRow: React.FC<{ food: FoodItem }> = ({ food }) => (
  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '4px 0' }}>
    <Space size={4}>
      <FireOutlined style={{ color: '#fa8c16', fontSize: 12 }} />
      <span style={{ fontSize: 13, color: '#333' }}>{food.name}</span>
      {food.estimatedGrams > 0 && <span style={{ fontSize: 12, color: '#999' }}>{food.estimatedGrams.toFixed(0)}g</span>}
      {food.confidence > 0 && (
        <span style={{
          fontSize: 10, color: '#fff', borderRadius: 4, padding: '0 5px',
          lineHeight: '18px', background: food.confidence >= 0.8 ? '#52c41a' : '#faad14',
        }}>
          {(food.confidence * 100).toFixed(0)}%
        </span>
      )}
    </Space>
    <Space size={10} style={{ fontSize: 12 }}>
      <span style={{ color: '#f5222d' }}>{food.calories.toFixed(0)} kcal</span>
      {food.protein > 0 && <span style={{ color: '#52c41a' }}>P {food.protein.toFixed(1)}</span>}
      {food.fat > 0 && <span style={{ color: '#fa8c16' }}>F {food.fat.toFixed(1)}</span>}
      {food.carbs > 0 && <span style={{ color: '#1890ff' }}>C {food.carbs.toFixed(1)}</span>}
    </Space>
  </div>
);

const InlineAnalysisCard: React.FC<{
  foodResult: FoodResult;
  isPartial: boolean;
  adopted: boolean;
}> = ({ foodResult, isPartial, adopted }) => {
  const mealInfo = mealTypeLabel[foodResult.mealType] ?? { label: foodResult.mealType || '未知', color: 'default' };

  return (
    <div style={{
      marginTop: 8,
      background: adopted ? '#f6ffed' : '#fffbe6',
      border: `1px solid ${adopted ? '#b7eb8f' : '#ffe58f'}`,
      borderRadius: 12,
      padding: '12px 14px',
    }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 8 }}>
        <Space size={6}>
          <Tag color={mealInfo.color} style={{ fontSize: 12, padding: '0 8px', lineHeight: '22px', margin: 0 }}>{mealInfo.label}</Tag>
          <Tag color={adopted ? 'success' : 'default'} style={{ fontSize: 11, padding: '0 6px', lineHeight: '20px', margin: 0 }}>
            {adopted ? '已采纳' : '已覆盖'}
          </Tag>
          {isPartial && <Tag color="processing" style={{ fontSize: 11, padding: '0 6px', lineHeight: '20px', margin: 0 }}>待补充</Tag>}
        </Space>
        <Space size={8}>
          <span style={{ fontWeight: 700, fontSize: 17, color: '#f5222d' }}>
            {foodResult.totalCalories.toFixed(0)} kcal
          </span>
          {foodResult.overallConfidence > 0 && (
            <span style={{
              fontSize: 11, color: foodResult.overallConfidence >= 0.8 ? '#52c41a' : '#faad14',
              background: foodResult.overallConfidence >= 0.8 ? '#f6ffed' : '#fffbe6',
              padding: '0 6px', borderRadius: 4, lineHeight: '20px',
            }}>
              {(foodResult.overallConfidence * 100).toFixed(0)}%
            </span>
          )}
        </Space>
      </div>

      {foodResult.foods.length > 0 && (
        <div style={{ borderTop: `1px solid ${adopted ? '#b7eb8f' : '#ffe58f'}`, paddingTop: 6 }}>
          {foodResult.foods.map((food, idx) => (
            <FoodItemRow key={idx} food={food} />
          ))}
        </div>
      )}

      {foodResult.totalProtein > 0 || foodResult.totalFat > 0 || foodResult.totalCarbs > 0 ? (
        <div style={{
          borderTop: `1px solid ${adopted ? '#b7eb8f' : '#ffe58f'}`, paddingTop: 6, marginTop: 6,
          display: 'flex', gap: 14, fontSize: 12, color: '#666',
        }}>
          {foodResult.totalProtein > 0 && <span style={{ color: '#52c41a' }}>蛋白质 {foodResult.totalProtein.toFixed(1)}g</span>}
          {foodResult.totalFat > 0 && <span style={{ color: '#fa8c16' }}>脂肪 {foodResult.totalFat.toFixed(1)}g</span>}
          {foodResult.totalCarbs > 0 && <span style={{ color: '#1890ff' }}>碳水 {foodResult.totalCarbs.toFixed(1)}g</span>}
        </div>
      ) : null}
    </div>
  );
};

const genderLabel: Record<string, string> = {
  male: '男',
  female: '女',
};

const activityLabel: Record<string, string> = {
  sedentary: '久坐',
  light: '轻度运动',
  moderate: '中度运动',
  active: '活跃',
  very_active: '非常活跃',
};

const exerciseTypeLabel: Record<string, string> = {
  cardio: '有氧运动',
  strength: '力量训练',
  flexibility: '柔韧性训练',
  sports: '运动',
  other: '其他',
};

const exerciseIntensityLabel: Record<string, string> = {
  low: '低强度',
  medium: '中等强度',
  high: '高强度',
};

const UserResultCard: React.FC<{ userResult: UserResult }> = ({ userResult }) => (
  <div style={{
    marginTop: 8,
    background: '#e6f7ff',
    border: '1px solid #91d5ff',
    borderRadius: 12,
    padding: '12px 14px',
  }}>
    <div style={{ marginBottom: 8 }}>
      <Tag color="blue" style={{ fontSize: 12, padding: '0 8px', lineHeight: '22px', margin: 0 }}>
        用户信息识别
      </Tag>
      {userResult.isComplete === false && (
        <Tag color="processing" style={{ fontSize: 11, padding: '0 6px', lineHeight: '20px', marginLeft: 6 }}>
          待补充
        </Tag>
      )}
    </div>
    <div style={{ display: 'flex', flexWrap: 'wrap', gap: '6px 16px', fontSize: 13, color: '#333', lineHeight: '24px' }}>
      {userResult.age != null && <span>年龄: <b>{userResult.age}</b> 岁</span>}
      {userResult.heightCm != null && <span>身高: <b>{userResult.heightCm}</b> cm</span>}
      {userResult.weightKg != null && <span>体重: <b>{userResult.weightKg}</b> kg</span>}
      {userResult.gender && <span>性别: <b>{genderLabel[userResult.gender] || userResult.gender}</b></span>}
      {userResult.activityLevel && <span>活动水平: <b>{activityLabel[userResult.activityLevel] || userResult.activityLevel}</b></span>}
    </div>
    {(userResult.targetWeightKg != null || userResult.dailyCalorieTarget != null ||
      userResult.proteinTarget != null || userResult.fatTarget != null || userResult.carbsTarget != null) && (
      <div style={{
        borderTop: '1px solid #91d5ff', paddingTop: 6, marginTop: 6,
        display: 'flex', flexWrap: 'wrap', gap: '6px 16px', fontSize: 13, color: '#333', lineHeight: '24px',
      }}>
        {userResult.targetWeightKg != null && <span>目标体重: <b>{userResult.targetWeightKg}</b> kg</span>}
        {userResult.dailyCalorieTarget != null && <span>每日热量目标: <b>{userResult.dailyCalorieTarget.toFixed(0)}</b> kcal</span>}
        {userResult.proteinTarget != null && <span>蛋白质目标: <b>{userResult.proteinTarget.toFixed(1)}</b> g</span>}
        {userResult.fatTarget != null && <span>脂肪目标: <b>{userResult.fatTarget.toFixed(1)}</b> g</span>}
        {userResult.carbsTarget != null && <span>碳水目标: <b>{userResult.carbsTarget.toFixed(1)}</b> g</span>}
      </div>
    )}
  </div>
);

const ExerciseResultCard: React.FC<{ exerciseResult: ExerciseResult }> = ({ exerciseResult }) => (
  <div style={{
    marginTop: 8,
    background: '#fff7e6',
    border: '1px solid #ffd591',
    borderRadius: 12,
    padding: '12px 14px',
  }}>
    <div style={{ marginBottom: 8 }}>
      <Tag color="orange" style={{ fontSize: 12, padding: '0 8px', lineHeight: '22px', margin: 0 }}>
        运动识别
      </Tag>
    </div>
    <div style={{ display: 'flex', flexWrap: 'wrap', gap: '6px 16px', fontSize: 13, color: '#333', lineHeight: '24px' }}>
      {exerciseResult.type && <span>类型: <b>{exerciseTypeLabel[exerciseResult.type] || exerciseResult.type}</b></span>}
      {exerciseResult.name && <span>名称: <b>{exerciseResult.name}</b></span>}
      {exerciseResult.durationMinutes != null && <span>时长: <b>{exerciseResult.durationMinutes}</b> 分钟</span>}
      {exerciseResult.distanceKm != null && <span>距离: <b>{exerciseResult.distanceKm.toFixed(1)}</b> km</span>}
      {exerciseResult.caloriesBurned != null && <span>消耗热量: <b>{exerciseResult.caloriesBurned.toFixed(0)}</b> kcal</span>}
      {exerciseResult.countValue != null && <span>次数: <b>{exerciseResult.countValue}</b>{exerciseResult.countUnit || ''}</span>}
      {exerciseResult.intensity && <span>强度: <b>{exerciseIntensityLabel[exerciseResult.intensity] || exerciseResult.intensity}</b></span>}
      {exerciseResult.date && <span>日期: <b>{exerciseResult.date}</b></span>}
      {exerciseResult.note && <span>备注: <b>{exerciseResult.note}</b></span>}
    </div>
  </div>
);

const WeightResultCard: React.FC<{ weightResult: WeightResult }> = ({ weightResult }) => (
  <div style={{
    marginTop: 8,
    background: '#f9f0ff',
    border: '1px solid #d3adf7',
    borderRadius: 12,
    padding: '12px 14px',
  }}>
    <div style={{ marginBottom: 8 }}>
      <Tag color="purple" style={{ fontSize: 12, padding: '0 8px', lineHeight: '22px', margin: 0 }}>
        体重识别
      </Tag>
    </div>
    <div style={{ display: 'flex', flexWrap: 'wrap', gap: '6px 16px', fontSize: 13, color: '#333', lineHeight: '24px' }}>
      {weightResult.weightKg != null && (
        <span>体重: <b style={{ fontSize: 16, color: '#722ed1' }}>{weightResult.weightKg}</b> kg</span>
      )}
      {weightResult.date && <span>日期: <b>{weightResult.date}</b></span>}
      {weightResult.note && <span>备注: <b>{weightResult.note}</b></span>}
    </div>
  </div>
);

const MessageModal: React.FC<{
  open: boolean;
  session: AdminSession | null;
  onClose: () => void;
}> = ({ open, session, onClose }) => {
  const [messages, setMessages] = useState<SessionMessage[]>([]);
  const [sessionResult, setSessionResult] = useState<LLMAnalysisResult | null>(null);
  const [loading, setLoading] = useState(false);
  const [expandedResults, setExpandedResults] = useState<Set<number>>(new Set());

  React.useEffect(() => {
    if (open && session) {
      setLoading(true);
      getSessionMessages(session.id)
        .then((res) => {
          setMessages(res.messages);
          setSessionResult(res.result);
        })
        .catch(() => message.error('获取会话消息失败'))
        .finally(() => setLoading(false));
    } else {
      setMessages([]);
      setSessionResult(null);
      setExpandedResults(new Set());
    }
  }, [open, session]);

  const toggleResultExpand = (msgId: number) => {
    setExpandedResults(prev => {
      const next = new Set(prev);
      if (next.has(msgId)) {
        next.delete(msgId);
      } else {
        next.add(msgId);
      }
      return next;
    });
  };

  const hasAnyResult = (result: LLMAnalysisResult | null | undefined) => {
    if (!result) return false;
    const hasFoodResult = result.foodResult && result.foodResult.foods && result.foodResult.foods.length > 0;
    const hasUserResult = result.userResult && (result.userResult.age != null || result.userResult.gender != null || result.userResult.heightCm != null || result.userResult.weightKg != null);
    const hasWeightResult = result.weightResult && result.weightResult.weightKg != null;
    const hasExerciseResult = result.exerciseResult && (result.exerciseResult.name != null || result.exerciseResult.type != null);
    return !!(hasFoodResult || hasUserResult || hasWeightResult || hasExerciseResult);
  };

  const renderResultCard = (result: LLMAnalysisResult, msgId: number, isAdopted: boolean) => {
    const hasFoodData = result.foodResult && result.foodResult.foods && result.foodResult.foods.length > 0;
    const hasUserData = result.userResult && (result.userResult.age != null || result.userResult.gender != null || result.userResult.heightCm != null || result.userResult.weightKg != null);
    const hasWeightData = result.weightResult && result.weightResult.weightKg != null;
    const hasExerciseData = result.exerciseResult && (result.exerciseResult.name != null || result.exerciseResult.type != null);
    return (
      <div style={{ marginLeft: 46, padding: '8px 0' }}>
        {hasFoodData && (
          <InlineAnalysisCard
            foodResult={result.foodResult!}
            isPartial={result.isPartial}
            adopted={isAdopted}
          />
        )}
        {hasUserData && <UserResultCard userResult={result.userResult!} />}
        {hasExerciseData && <ExerciseResultCard exerciseResult={result.exerciseResult!} />}
        {hasWeightData && <WeightResultCard weightResult={result.weightResult!} />}
      </div>
    );
  };

  return (
    <Modal
      title="会话详情"
      open={open}
      onCancel={onClose}
      footer={null}
      width={760}
      styles={{ body: { padding: '12px 24px', maxHeight: '70vh', overflow: 'auto' } }}
    >
      {loading ? (
        <div style={{ textAlign: 'center', padding: '60px 0' }}>
          <Spin />
        </div>
      ) : messages.length === 0 ? (
        <Empty description="暂无消息" />
      ) : (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
          {messages.map((msg, idx) => {
            const isUser = msg.role === 'user';
            const isExpanded = expandedResults.has(msg.id);
            const canExpand = !isUser && hasAnyResult(msg.result);
            return (
              <React.Fragment key={msg.id}>
                <div
                  style={{
                    display: 'flex',
                    alignItems: 'flex-start',
                    gap: 10,
                    flexDirection: isUser ? 'row-reverse' : 'row',
                  }}
                >
                  <Avatar
                    size={36}
                    icon={isUser ? <UserOutlined /> : <RobotOutlined />}
                    style={{
                      flexShrink: 0,
                      backgroundColor: isUser ? '#1890ff' : '#52c41a',
                    }}
                  />
                  <div style={{ maxWidth: '70%', position: 'relative' }}>
                    {canExpand && (
                      <QuestionCircleOutlined
                        onClick={() => toggleResultExpand(msg.id)}
                        style={{
                          position: 'absolute',
                          top: -8,
                          right: -8,
                          fontSize: 18,
                          color: '#faad14',
                          cursor: 'pointer',
                          zIndex: 1,
                          opacity: isExpanded ? 1 : 0.7,
                        }}
                      />
                    )}
                    <div
                      style={{
                        padding: '10px 14px',
                        borderRadius: 12,
                        fontSize: 14,
                        lineHeight: 1.6,
                        wordBreak: 'break-word',
                        whiteSpace: 'pre-wrap',
                        backgroundColor: isUser ? '#e6f7ff' : '#f6f6f6',
                        color: '#333',
                        borderTopLeftRadius: isUser ? 12 : 4,
                        borderTopRightRadius: isUser ? 4 : 12,
                      }}
                    >
                      {msg.content || '(空)'}
                    </div>
                  </div>
                </div>
                {isExpanded && !isUser && msg.result && renderResultCard(msg.result, msg.id, false)}
              </React.Fragment>
            );
          })}
          {sessionResult && hasAnyResult(sessionResult) && (
            <div style={{ marginTop: 8, borderTop: '1px solid #f0f0f0', paddingTop: 16 }}>
              <div style={{ fontSize: 13, color: '#999', marginBottom: 8 }}>最终识别结果</div>
              {sessionResult.foodResult && sessionResult.foodResult.foods && sessionResult.foodResult.foods.length > 0 && (
                <InlineAnalysisCard
                  foodResult={sessionResult.foodResult}
                  isPartial={sessionResult.isPartial}
                  adopted={true}
                />
              )}
              {sessionResult.userResult && (sessionResult.userResult.age != null || sessionResult.userResult.gender != null || sessionResult.userResult.heightCm != null || sessionResult.userResult.weightKg != null) && (
                <UserResultCard userResult={sessionResult.userResult} />
              )}
              {sessionResult.weightResult && sessionResult.weightResult.weightKg != null && (
                <WeightResultCard weightResult={sessionResult.weightResult} />
              )}
              {sessionResult.exerciseResult && (sessionResult.exerciseResult.name != null || sessionResult.exerciseResult.type != null) && (
                <ExerciseResultCard exerciseResult={sessionResult.exerciseResult} />
              )}
            </div>
          )}
        </div>
      )}
    </Modal>
  );
};

const Sessions: React.FC = () => {
  const actionRef = useRef<ActionType>();
  const [userId, setUserId] = useState('');
  const [startTime, setStartTime] = useState('');
  const [endTime, setEndTime] = useState('');
  const [modalOpen, setModalOpen] = useState(false);
  const [selectedSession, setSelectedSession] = useState<AdminSession | null>(null);

  const columns: ProColumns<AdminSession>[] = [
    {
      title: '用户信息',
      width: 180,
      render: (_, record) => (
        <Space>
          <Avatar size={28} icon={<UserOutlined />} src={record.avatarUrl} />
          <span>{record.nickname || `用户#${record.userId}`}</span>
        </Space>
      ),
    },
    {
      title: '用户ID',
      dataIndex: 'userId',
      width: 100,
      copyable: true,
    },
    {
      title: '状态',
      dataIndex: 'status',
      width: 110,
      render: (_, record) => {
        const s = statusMap[record.status] || { color: 'default', text: record.status };
        return <Tag color={s.color}>{s.text}</Tag>;
      },
    },
    {
      title: '会话轮次',
      dataIndex: 'rounds',
      width: 90,
      align: 'center',
    },
    {
      title: '关联饮食记录',
      width: 130,
      align: 'center',
      render: (_, record) =>
        record.foodLogId ? (
          <Tag color="blue" style={{ cursor: 'pointer' }}>
            #{record.foodLogId}
          </Tag>
        ) : (
          <span style={{ color: '#999' }}>-</span>
        ),
    },
    {
      title: '会话时间',
      dataIndex: 'createdAt',
      width: 180,
      render: (_, record) => dayjs(record.createdAt * 1000).format('YYYY-MM-DD HH:mm:ss'),
    },
    {
      title: '操作',
      width: 100,
      fixed: 'right',
      render: (_, record) => (
        <Button
          type="link"
          icon={<MessageOutlined />}
          onClick={() => {
            setSelectedSession(record);
            setModalOpen(true);
          }}
        >
          查看详情
        </Button>
      ),
    },
  ];

  return (
    <>
      <div style={{ height: '100%', display: 'flex', flexDirection: 'column' }}>
        <ProTable<AdminSession>
          actionRef={actionRef}
          columns={columns}
          request={async (params) => {
            const res = await getAdminSessions({
              userId: userId || undefined,
              startTime: startTime || undefined,
              endTime: endTime || undefined,
              page: params.current,
              pageSize: params.pageSize,
            });
            return {
              data: res.items,
              success: true,
              total: res.total,
            };
          }}
          rowKey="id"
          pagination={{ pageSize: 10, showSizeChanger: true }}
          search={false}
          scroll={{ x: 'max-content', y: 'calc(100vh - 280px)' }}
          toolbar={{
            search: (
              <Space wrap>
                <Input
                  placeholder="用户ID"
                  value={userId}
                  onChange={(e) => setUserId(e.target.value)}
                  style={{ width: 140 }}
                  allowClear
                />
                <Input
                  type="date"
                  placeholder="开始时间"
                  value={startTime}
                  onChange={(e) => setStartTime(e.target.value)}
                  style={{ width: 160 }}
                />
                <Input
                  type="date"
                  placeholder="结束时间"
                  value={endTime}
                  onChange={(e) => setEndTime(e.target.value)}
                  style={{ width: 160 }}
                />
                <Button
                  type="primary"
                  icon={<SearchOutlined />}
                  onClick={() => actionRef.current?.reload()}
                >
                  搜索
                </Button>
              </Space>
            ),
            settings: [],
          }}
        />
      </div>
      <MessageModal
        open={modalOpen}
        session={selectedSession}
        onClose={() => {
          setModalOpen(false);
          setSelectedSession(null);
        }}
      />
    </>
  );
};

export default Sessions;
